import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Filter,
  X,
  Activity,
  Zap,
  FileText,
  Database,
  ArrowDownToLine,
  ArrowUpFromLine,
  Layers,
} from 'lucide-react';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from 'recharts';
import baseapi from '../../../services/baseapi';
import { ItemsPerPageDropdown } from '../../../components/ItemsPerPageDropdown ';
import DBTypeIcon from '../../../components/DBTypeIcon';

interface MongoFileIOViewProps {
  activeCluster: string;
  timeRange?: string;
  clientId?: string;
  refreshKey?: number;
  dbType?: string;
}

// const ScrollableLegend = (props: any) => {
//   const { payload, hoveredSeries, onHoverSeries } = props;
//   if (!payload || payload.length === 0) return null;
//   return (
//     <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 max-h-16 overflow-y-auto custom-scroll px-2 py-1 relative z-0">
//       {payload.map((entry: any, index: number) => {
//         const seriesKey = entry.dataKey || entry.value;
//         const isHovered = hoveredSeries === seriesKey;
//         const isDimmed = hoveredSeries !== null && !isHovered;
//         return (
//           <div
//             key={`legend-item-${index}`}
//             onMouseEnter={() => onHoverSeries && onHoverSeries(seriesKey)}
//             onMouseLeave={() => onHoverSeries && onHoverSeries(null)}
//             className={`flex items-center gap-1.5 shrink-0 cursor-pointer transition-all duration-200 ${isDimmed ? 'opacity-30 scale-95' : 'opacity-100 scale-95'
//               }`}
//           >
//             <span
//               className="w-2.5 h-2.5 rounded-full inline-block shrink-0 shadow-sm"
//               style={{ backgroundColor: entry.color }}
//             />
//             <span
//               className="text-gray-700 dark:text-gray-200 text-xs truncate max-w-[160px] font-mono font-medium"
//               title={entry.value}
//             >
//               {entry.value}
//             </span>
//           </div>
//         );
//       })}
//     </div>
//   );
// };

const ChartTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border border-gray-200 dark:border-gray-700/80 rounded-lg p-2.5 shadow-xl text-[11px] text-gray-900 dark:text-gray-100 flex flex-col gap-1.5 min-w-[130px] z-50">
        {label !== undefined && label !== null && label !== "" && (
          <div className="text-gray-700 dark:text-gray-300 font-bold border-b border-gray-100 dark:border-gray-800 pb-1 mb-0.5 text-[11px] truncate">
            {label}
          </div>
        )}
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300 font-medium">
              <span
                className="inline-block w-2 h-2 rounded-full shrink-0"
                style={{ backgroundColor: entry.color || entry.stroke || entry.fill || "#10b981" }}
              />
              {entry.name || entry.dataKey}
            </span>
            <span className="font-bold text-gray-900 dark:text-white font-mono">
              {typeof entry.value === 'number' ? entry.value.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : (entry.value ?? '-')}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const MongoFileIOView: React.FC<MongoFileIOViewProps> = ({
  activeCluster,
  timeRange,
  clientId,
  refreshKey,
  dbType = 'mongodb',
}) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [fileIOData, setFileIOData] = useState<any>(null);
  // const [metricFilter, setMetricFilter] = useState<'all' | 'total' | 'read' | 'write'>('all');
  const [selectedDbFilter, setSelectedDbFilter] = useState<string>('all');
  const [tableSearchQuery, setTableSearchQuery] = useState<string>('');

  // Pagination for Files table
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);

  const fetchData = useCallback(async () => {
    if (!activeCluster) return;
    setLoading(true);

    const minutes = parseInt(timeRange || '60', 10);
    const params: any = {
      cluster_id: activeCluster,
      
      minutes,
    };
    if (clientId) {
      params.client_id = clientId;
    }

    try {
      const res = await baseapi.get('/db_monitor_dashboard/graph/mongodb/io', { params });
      setFileIOData(res.data?.data || res.data);
    } catch (err) {
      console.warn('MongoFileIOView fetch error:', err);
      setFileIOData(null);
    } finally {
      setLoading(false);
    }
  }, [activeCluster, timeRange, clientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData, refreshKey]);

  const labels: string[] = fileIOData?.labels || [];
  const series: Record<string, (number | null)[]> = fileIOData?.series || {};
  const summary = fileIOData?.summary;

  const formatTimeLabels = (lbls: string[]) => {
    if (!lbls || lbls.length === 0) return [];
    const minutes = parseInt(timeRange || '60', 10);
    const seen: { [key: string]: number } = {};
    return lbls.map((lbl) => {
      const d = new Date(lbl);
      let base = isNaN(d.getTime())
        ? lbl
        : minutes > 1440
          ? d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

      if (seen[base] !== undefined) {
        seen[base] += 1;
        base = `${base}${' '.repeat(seen[base])}`;
      } else {
        seen[base] = 0;
      }
      return base;
    });
  };

  const timeLabels = useMemo(() => formatTimeLabels(labels), [labels, timeRange]);

  // Formatted data points for recharts
  const formattedData = useMemo(() => {
    if (!labels || labels.length === 0) return [];
    return labels.map((_, idx) => {
      const readVal = series.read_kbps?.[idx];
      const writeVal = series.write_kbps?.[idx];
      const totalVal = series.total_kbps?.[idx];

      const readNum = readVal !== undefined && readVal !== null ? Number(Number(readVal).toFixed(2)) : 0;
      const writeNum = writeVal !== undefined && writeVal !== null ? Number(Number(writeVal).toFixed(2)) : 0;
      const totalNum =
        totalVal !== undefined && totalVal !== null
          ? Number(Number(totalVal).toFixed(2))
          : Number((readNum + writeNum).toFixed(2));

      const point: Record<string, any> = {
        time: timeLabels[idx] || labels[idx],
        'Read (KB/s)': readNum,
        'Write (KB/s)': writeNum,
        'Total I/O (KB/s)': totalNum,
      };

      // Also attach any dynamic extra series if present
      Object.keys(series).forEach((key) => {
        if (!['read_kbps', 'write_kbps', 'total_kbps'].includes(key)) {
          const val = series[key]?.[idx];
          point[key] = val !== undefined && val !== null ? Number(Number(val).toFixed(2)) : 0;
        }
      });

      return point;
    });
  }, [labels, series, timeLabels]);

  const hasData = (arr: any[]) => {
    if (!arr || arr.length === 0) return false;
    return arr.some((item) =>
      Object.keys(item).some(
        (k) => k !== 'time' && typeof item[k] === 'number' && item[k] > 0
      )
    );
  };



  const formatKBps = (kbps: number | undefined | null) => {
    if (kbps === undefined || kbps === null || isNaN(kbps)) return '0.00 KB/s';
    if (kbps >= 1024 * 1024) {
      return `${(kbps / (1024 * 1024)).toFixed(2)} GB/s`;
    }
    if (kbps >= 1024) {
      return `${(kbps / 1024).toFixed(2)} MB/s`;
    }
    return `${Number(kbps).toFixed(2)} KB/s`;
  };

  // Safe fallback calculation for read/write summary metrics
  const readMetrics = useMemo(() => {
    if (summary?.read) {
      return {
        current: summary.read.current_kbps ?? 0,
        peak: summary.read.peak_kbps ?? 0,
        avg: summary.read.average_kbps ?? 0,
      };
    }
    const arr = (series.read_kbps || []).filter((v): v is number => v !== null && v !== undefined);
    const curr = arr.length > 0 ? arr[arr.length - 1] : 0;
    const peak = arr.length > 0 ? Math.max(...arr) : 0;
    const avg = arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    return { current: curr, peak, avg };
  }, [summary, series]);

  const writeMetrics = useMemo(() => {
    if (summary?.write) {
      return {
        current: summary.write.current_kbps ?? 0,
        peak: summary.write.peak_kbps ?? 0,
        avg: summary.write.average_kbps ?? 0,
      };
    }
    const arr = (series.write_kbps || []).filter((v): v is number => v !== null && v !== undefined);
    const curr = arr.length > 0 ? arr[arr.length - 1] : 0;
    const peak = arr.length > 0 ? Math.max(...arr) : 0;
    const avg = arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : 0;
    return { current: curr, peak, avg };
  }, [summary, series]);

  const totalCurrentIO = summary?.current_io_kbps ?? (readMetrics.current + writeMetrics.current);
  const totalPeakIO = summary?.peak_io_kbps ?? (readMetrics.peak + writeMetrics.peak);
  const totalAvgIO = summary?.average_io_kbps ?? (readMetrics.avg + writeMetrics.avg);

  // Read/Write percentage calculation
  const readPercentage = useMemo(() => {
    const total = totalAvgIO || (readMetrics.avg + writeMetrics.avg);
    if (total <= 0) return 50;
    return Math.round((readMetrics.avg / total) * 100);
  }, [totalAvgIO, readMetrics, writeMetrics]);

  const writePercentage = 100 - readPercentage;

  // Available database options from summary or files
  const availableDatabases = useMemo(() => {
    const dbs = new Set<string>();
    if (summary?.database_distribution) {
      summary.database_distribution.forEach((d: any) => {
        if (d.database) dbs.add(d.database);
      });
    }
    if (summary?.files) {
      summary.files.forEach((f: any) => {
        if (f.database) dbs.add(f.database);
      });
    }
    return Array.from(dbs).sort();
  }, [summary]);

  // Files list
  const allFilesList: any[] = useMemo(() => {
    if (Array.isArray(summary?.files) && summary.files.length > 0) {
      return summary.files;
    }
    return [];
  }, [summary]);

  // Filtered files by database & table search query
  const filteredFiles = useMemo(() => {
    return allFilesList.filter((f) => {
      const matchesDb =
        selectedDbFilter === 'all' ||
        (f.database && f.database.toLowerCase() === selectedDbFilter.toLowerCase());
      const matchesSearch =
        !tableSearchQuery.trim() ||
        (f.file_name && f.file_name.toLowerCase().includes(tableSearchQuery.toLowerCase().trim())) ||
        (f.database && f.database.toLowerCase().includes(tableSearchQuery.toLowerCase().trim()));
      return matchesDb && matchesSearch;
    });
  }, [allFilesList, selectedDbFilter, tableSearchQuery]);

  // Paginated files
  const totalPages = Math.ceil(filteredFiles.length / itemsPerPage) || 1;
  const paginatedFiles = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredFiles.slice(start, start + itemsPerPage);
  }, [filteredFiles, currentPage, itemsPerPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedDbFilter, tableSearchQuery, itemsPerPage]);

  const getStatusBadge = (status: string) => {
    const st = (status || summary?.status || 'Normal').toLowerCase();
    if (st === 'high' || st === 'critical') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase bg-red-50 dark:bg-red-500/20 text-red-700 dark:text-red-400 border border-red-300 dark:border-red-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-ping"></span>
          High
        </span>
      );
    }
    if (st === 'elevated' || st === 'warning') {
      return (
        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400 border border-amber-300 dark:border-amber-500/30">
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
          Elevated
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-500/30">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
        Normal
      </span>
    );
  };

  const getTypeBadge = (type: string) => {
    const t = (type || 'COLLECTION').toUpperCase();
    if (t.includes('JOURNAL') || t.includes('LOG')) {
      return (
        <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-purple-50 dark:bg-purple-500/20 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-500/30">
          JOURNAL
        </span>
      );
    }
    if (t.includes('INDEX')) {
      return (
        <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-amber-50 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-500/30">
          INDEX
        </span>
      );
    }
    return (
      <span className="inline-block px-2 py-0.5 rounded text-[9px] font-bold uppercase bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30">
        DATA
      </span>
    );
  };

  const maxDbPeak = useMemo(() => {
    if (!summary?.database_distribution || summary.database_distribution.length === 0) return 1;
    const max = Math.max(...summary.database_distribution.map((d: any) => Number(d.peak_kbps) || 0));
    return max > 0 ? max : 1;
  }, [summary]);

  if (loading) {
    return (
      <div className="min-h-[400px] w-full flex items-center justify-center bg-white/60 dark:bg-gray-900/40 rounded-xl border border-gray-200 dark:border-gray-800 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
          <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">Loading MongoDB File I/O telemetry...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Top Banner Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-gray-900/60 p-3.5 rounded-xl border border-gray-200 dark:border-gray-800/80 shadow-sm dark:shadow-md">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 shadow-sm flex items-center justify-center">
            <DBTypeIcon dbType={dbType} className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-base font-bold text-gray-900 dark:text-white leading-tight">MongoDB File I/O</h2>
              <span className="px-2 py-0.5 text-[10px] font-semibold bg-emerald-50 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-500/30 rounded">
                WiredTiger Engine
              </span>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Disk read/write throughput and storage engine activity telemetry
            </p>
          </div>
        </div>

        {/* Header Right: Status Badge & Sample Info */}
        <div className="flex items-center gap-3">
          <div className="text-[11px] text-gray-500 dark:text-gray-400 hidden sm:block font-mono">
            <span className="font-semibold text-gray-700 dark:text-gray-200">{labels.length}</span> samples
            {labels.length > 0 && (
              <>
                {' '}• Last sample: <span className="text-emerald-600 dark:text-emerald-400">{timeLabels[timeLabels.length - 1]}</span>
              </>
            )}
          </div>
          {getStatusBadge(summary?.status)}
        </div>
      </div>

      {/* Top 4 KPI Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2.5">
        {/* TOTAL CURRENT I/O */}
        <div className="bg-blue-50/70 dark:bg-gray-900/90 border border-blue-200 dark:border-gray-700/60 rounded-lg py-2 px-3 shadow-sm flex flex-col justify-between hover:border-blue-500/50 transition-all">
          <div>
            <div className="flex items-center justify-between gap-1 mb-1">
              <span className="text-[10px] font-bold text-blue-700 dark:text-gray-400 uppercase tracking-wider truncate">
                Total I/O Throughput
              </span>
              <Activity className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400 shrink-0" />
            </div>
            <div className="text-sm font-bold font-mono text-gray-900 dark:text-white tracking-tight leading-tight my-0.5">
              {formatKBps(totalCurrentIO)}
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-400 mt-1.5 pt-1.5 border-t border-blue-100 dark:border-gray-800/60 leading-tight">
            <span>Peak: <strong className="text-gray-900 dark:text-gray-200 font-mono">{formatKBps(totalPeakIO)}</strong></span>
            <span>Avg: <strong className="text-gray-900 dark:text-gray-200 font-mono">{formatKBps(totalAvgIO)}</strong></span>
          </div>
        </div>

        {/* READ I/O */}
        <div className="bg-emerald-50/70 dark:bg-gray-900/90 dark:to-gray-900/60 border border-emerald-200 dark:border-gray-700/60 rounded-lg py-2 px-3 shadow-sm flex flex-col justify-between hover:border-emerald-500/50 transition-all">
          <div>
            <div className="flex items-center justify-between gap-1 mb-1">
              <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider flex items-center gap-1 truncate">
                <ArrowDownToLine className="w-3 h-3 shrink-0" />
                Read Throughput
              </span>
              <span className="text-[9px] font-mono text-emerald-700 dark:text-emerald-400 font-semibold bg-emerald-100/80 dark:bg-emerald-500/10 px-1.5 py-0.5 rounded border border-emerald-200 dark:border-emerald-500/20 shrink-0 leading-none">
                {readPercentage}% I/O
              </span>
            </div>
            <div className="text-sm font-bold font-mono text-emerald-600 dark:text-emerald-400 tracking-tight leading-tight my-0.5">
              {formatKBps(readMetrics.current)}
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-400 mt-1.5 pt-1.5 border-t border-emerald-100 dark:border-gray-800/60 leading-tight">
            <span>Peak: <strong className="text-gray-900 dark:text-gray-200 font-mono">{formatKBps(readMetrics.peak)}</strong></span>
            <span>Avg: <strong className="text-gray-900 dark:text-gray-200 font-mono">{formatKBps(readMetrics.avg)}</strong></span>
          </div>
        </div>

        {/* WRITE I/O */}
        <div className="bg-amber-50/70 dark:bg-gray-900/90 dark:to-gray-900/60 border border-amber-200 dark:border-gray-700/60 rounded-lg py-2 px-3 shadow-sm flex flex-col justify-between hover:border-amber-500/50 transition-all">
          <div>
            <div className="flex items-center justify-between gap-1 mb-1">
              <span className="text-[10px] font-bold text-amber-700 dark:text-amber-400 uppercase tracking-wider flex items-center gap-1 truncate">
                <ArrowUpFromLine className="w-3 h-3 shrink-0" />
                Write Throughput
              </span>
              <span className="text-[9px] font-mono text-amber-700 dark:text-amber-400 font-semibold bg-amber-100/80 dark:bg-amber-500/10 px-1.5 py-0.5 rounded border border-amber-200 dark:border-amber-500/20 shrink-0 leading-none">
                {writePercentage}% I/O
              </span>
            </div>
            <div className="text-sm font-bold font-mono text-amber-600 dark:text-amber-400 tracking-tight leading-tight my-0.5">
              {formatKBps(writeMetrics.current)}
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-400 mt-1.5 pt-1.5 border-t border-amber-100 dark:border-gray-800/60 leading-tight">
            <span>Peak: <strong className="text-gray-900 dark:text-gray-200 font-mono">{formatKBps(writeMetrics.peak)}</strong></span>
            <span>Avg: <strong className="text-gray-900 dark:text-gray-200 font-mono">{formatKBps(writeMetrics.avg)}</strong></span>
          </div>
        </div>

        {/* SCOPE & ENGINE ACTIVITY */}
        <div className="bg-purple-50/70 dark:bg-gray-900/90 dark:to-gray-900/60 border border-purple-200 dark:border-gray-700/60 rounded-lg py-2 px-3 shadow-sm flex flex-col justify-between hover:border-purple-500/50 transition-all">
          <div>
            <div className="flex items-center justify-between gap-1 mb-1">
              <span className="text-[10px] font-bold text-purple-700 dark:text-gray-400 uppercase tracking-wider truncate">
                Storage Scope
              </span>
              <Layers className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400 shrink-0" />
            </div>
            <div className="text-sm font-bold font-mono text-purple-700 dark:text-purple-300 capitalize leading-tight my-0.5 truncate">
              {summary?.scope || 'Instance Level'}
            </div>
          </div>
          <div className="flex items-center justify-between text-[10px] text-gray-600 dark:text-gray-400 mt-1.5 pt-1.5 border-t border-purple-100 dark:border-gray-800/60 leading-tight">
            <span>Files: <strong className="text-gray-900 dark:text-gray-200 font-mono">{summary?.active_files ?? allFilesList.length}</strong></span>
            <span>Status: <strong className="text-emerald-600 dark:text-emerald-400 font-mono">{summary?.status || 'Normal'}</strong></span>
          </div>
        </div>
      </div>

      {/* Main Chart: Combined I/O Throughput */}
      {/* <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3 border-b border-gray-200 dark:border-gray-700/50 pb-3">
          <div>
            <div className="flex items-center gap-2">
              <BarChart2 className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                Storage Engine I/O Timeline
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              Continuous read, write, and cumulative storage throughput
            </p>
          </div>

         
          <div className="flex items-center gap-1.5 bg-gray-100 dark:bg-gray-900/90 p-1 rounded-lg border border-gray-200 dark:border-gray-700/80">
            <button
              onClick={() => setMetricFilter('all')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${metricFilter === 'all'
                  ? 'bg-emerald-600 text-white font-bold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              All Series
            </button>
            <button
              onClick={() => setMetricFilter('total')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${metricFilter === 'total'
                  ? 'bg-blue-600 text-white font-bold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Total I/O
            </button>
            <button
              onClick={() => setMetricFilter('read')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${metricFilter === 'read'
                  ? 'bg-emerald-600 text-white font-bold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Read Only
            </button>
            <button
              onClick={() => setMetricFilter('write')}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all cursor-pointer ${metricFilter === 'write'
                  ? 'bg-amber-600 text-white font-bold shadow-sm'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                }`}
            >
              Write Only
            </button>
          </div>
        </div>

        <div className="h-72 w-full flex items-center justify-center">
          {hasData(formattedData) ? (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={formattedData}>
                <defs>
                  <linearGradient id="mongoTotalGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="mongoReadGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                  </linearGradient>
                  <linearGradient id="mongoWriteGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.25} />
                <XAxis dataKey="time" stroke="#9ca3af" fontSize={10} tickLine={false} />
                <YAxis stroke="#9ca3af" fontSize={10} tickLine={false} tickCount={5}>
                  <Label
                    value="Throughput (KB/s)"
                    angle={-90}
                    position="insideLeft"
                    offset={10}
                    fill="#9ca3af"
                    fontSize={10}
                  />
                </YAxis>
                <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                <Legend
                  content={
                    <ScrollableLegend
                      hoveredSeries={hoveredSeries}
                      onHoverSeries={setHoveredSeries}
                    />
                  }
                />

                {(metricFilter === 'all' || metricFilter === 'total') && (
                  <Area
                    type="monotone"
                    dataKey="Total I/O (KB/s)"
                    name="Total I/O (KB/s)"
                    stroke="#3b82f6"
                    strokeWidth={hoveredSeries === 'Total I/O (KB/s)' ? 2.5 : 2}
                    fill="url(#mongoTotalGrad)"
                    fillOpacity={getOpacity(hoveredSeries, 'Total I/O (KB/s)')}
                    strokeOpacity={getOpacity(hoveredSeries, 'Total I/O (KB/s)')}
                    dot={false}
                  />
                )}

                {(metricFilter === 'all' || metricFilter === 'read') && (
                  <Area
                    type="monotone"
                    dataKey="Read (KB/s)"
                    name="Read (KB/s)"
                    stroke="#10b981"
                    strokeWidth={hoveredSeries === 'Read (KB/s)' ? 2.5 : 2}
                    fill="url(#mongoReadGrad)"
                    fillOpacity={getOpacity(hoveredSeries, 'Read (KB/s)')}
                    strokeOpacity={getOpacity(hoveredSeries, 'Read (KB/s)')}
                    dot={false}
                  />
                )}

                {(metricFilter === 'all' || metricFilter === 'write') && (
                  <Area
                    type="monotone"
                    dataKey="Write (KB/s)"
                    name="Write (KB/s)"
                    stroke="#f59e0b"
                    strokeWidth={hoveredSeries === 'Write (KB/s)' ? 2.5 : 2}
                    fill="url(#mongoWriteGrad)"
                    fillOpacity={getOpacity(hoveredSeries, 'Write (KB/s)')}
                    strokeOpacity={getOpacity(hoveredSeries, 'Write (KB/s)')}
                    dot={false}
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center text-gray-400 dark:text-gray-500 py-12">
              <HardDrive className="w-8 h-8 text-gray-400 dark:text-gray-600 mb-2 opacity-50" />
              <span className="text-xs italic">No MongoDB file I/O telemetry data available</span>
            </div>
          )}
        </div>
      </div> */}

      {/* Dual Detail Charts: Read vs Write Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Read Rate Chart */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2.5 mb-3">
            <div className="flex items-center gap-2">
              <ArrowDownToLine className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              <div>
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Read Throughput Rate
                </h4>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Disk and buffer reads over time</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono font-bold text-emerald-600 dark:text-emerald-400">
                {formatKBps(readMetrics.current)}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                Peak: {formatKBps(readMetrics.peak)}
              </div>
            </div>
          </div>
          <div className="h-48 w-full">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="splitReadGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                  <Area
                    type="monotone"
                    dataKey="Read (KB/s)"
                    stroke="#10b981"
                    strokeWidth={2}
                    fill="url(#splitReadGrad)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 italic">
                No read telemetry
              </div>
            )}
          </div>
        </div>

        {/* Write Rate Chart */}
        <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
          <div className="flex items-center justify-between border-b border-gray-200 dark:border-gray-700/50 pb-2.5 mb-3">
            <div className="flex items-center gap-2">
              <ArrowUpFromLine className="w-4 h-4 text-amber-500 dark:text-amber-400" />
              <div>
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Write Throughput Rate
                </h4>
                <p className="text-[10px] text-gray-500 dark:text-gray-400">Journal and data writes over time</p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs font-mono font-bold text-amber-600 dark:text-amber-400">
                {formatKBps(writeMetrics.current)}
              </div>
              <div className="text-[10px] text-gray-500 dark:text-gray-400 font-mono">
                Peak: {formatKBps(writeMetrics.peak)}
              </div>
            </div>
          </div>
          <div className="h-48 w-full">
            {hasData(formattedData) ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={formattedData}>
                  <defs>
                    <linearGradient id="splitWriteGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" opacity={0.2} />
                  <XAxis dataKey="time" stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <YAxis stroke="#9ca3af" fontSize={9} tickLine={false} />
                  <Tooltip content={<ChartTooltip />} wrapperStyle={{ zIndex: 1000 }} />
                  <Area
                    type="monotone"
                    dataKey="Write (KB/s)"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    fill="url(#splitWriteGrad)"
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-xs text-gray-400 dark:text-gray-500 italic">
                No write telemetry
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Database Distribution & High Activity Files (when available) */}
      {(summary?.database_distribution?.length > 0 || summary?.highest_io_files?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {summary?.database_distribution?.length > 0 && (
            <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
              <div className="flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
                <Database className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Database I/O Distribution
                </h4>
              </div>
              <div className="space-y-3.5 max-h-64 overflow-y-auto custom-scroll pr-1">
                {summary.database_distribution.map((d: any, idx: number) => {
                  const pct = Math.min(100, Math.max(2, (Number(d.peak_kbps || 0) / maxDbPeak) * 100));
                  return (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-semibold text-gray-800 dark:text-gray-200 font-mono">{d.database}</span>
                        <span className="font-mono text-gray-500 dark:text-gray-400 text-[11px]">
                          {formatKBps(d.peak_kbps)} <span className="text-[10px] text-gray-400 dark:text-gray-500">peak</span>
                        </span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 dark:bg-gray-900 rounded-full overflow-hidden border border-gray-200 dark:border-gray-800">
                        <div
                          className="h-full bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full transition-all duration-300"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {summary?.highest_io_files?.length > 0 && (
            <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl">
              <div className="flex items-center gap-1.5 border-b border-gray-200 dark:border-gray-700/50 pb-2 mb-3">
                <Zap className="w-4 h-4 text-amber-500 dark:text-amber-400" />
                <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                  Highest I/O Files / Collections
                </h4>
              </div>
              <div className="space-y-2.5 max-h-64 overflow-y-auto custom-scroll pr-1">
                {summary.highest_io_files.map((hf: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between border-b border-gray-100 dark:border-gray-800/60 pb-2 last:border-0 last:pb-0"
                  >
                    <div>
                      <div className="text-xs font-bold text-gray-900 dark:text-white font-mono">{hf.file_name}</div>
                      <div className="text-[10px] text-gray-500 dark:text-gray-400">{hf.database}</div>
                    </div>
                    <div className="text-xs font-bold font-mono text-amber-600 dark:text-amber-400">
                      {formatKBps(hf.peak_kbps)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Storage Files Table or Instance Metrics Table */}
      <div className="bg-white dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700/60 rounded-xl p-4 shadow-sm dark:shadow-xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 dark:border-gray-700/50 pb-2.5">
          <div>
            <div className="flex items-center gap-1.5">
              <FileText className="w-4 h-4 text-emerald-500 dark:text-emerald-400" />
              <h4 className="text-xs font-bold text-gray-900 dark:text-white uppercase tracking-wider">
                {allFilesList.length > 0 ? 'MongoDB Storage Files' : 'Instance I/O Telemetry Summary'}
              </h4>
            </div>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
              {allFilesList.length > 0
                ? 'File-level read/write throughput breakdown'
                : 'Aggregated storage engine rate metrics across time window'}
            </p>
          </div>

          {allFilesList.length > 0 && (
            <div className="flex items-center gap-3">
              {/* Database selector if multiple databases exist */}
              {availableDatabases.length > 0 && (
                <div className="flex items-center gap-2 bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg px-2.5 py-1 shadow-sm">
                  <Filter className="w-3.5 h-3.5 text-gray-400" />
                  <select
                    value={selectedDbFilter}
                    onChange={(e) => setSelectedDbFilter(e.target.value)}
                    className="bg-transparent text-xs text-gray-900 dark:text-white focus:outline-none cursor-pointer pr-1"
                  >
                    <option value="all" className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">All Databases</option>
                    {availableDatabases.map((db) => (
                      <option key={db} value={db} className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white">
                        {db}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Search Input for Files */}
              <div className="relative">
                <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search file or db..."
                  value={tableSearchQuery}
                  onChange={(e) => setTableSearchQuery(e.target.value)}
                  className="bg-gray-50 dark:bg-gray-900/90 border border-gray-200 dark:border-gray-700 rounded-lg pl-8 pr-7 py-1.5 text-xs text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:border-emerald-500/50 w-52 h-8"
                />
                {tableSearchQuery && (
                  <button
                    onClick={() => setTableSearchQuery('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-white"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
            </div>
          )}
        </div>

        {allFilesList.length > 0 ? (
          <>
            <div className="overflow-x-auto overflow-y-auto custom-scroll border border-gray-200 dark:border-gray-700/50 rounded-lg">
              <table className="w-full text-left text-xs border-collapse">
                <thead className="sticky top-0 z-10">
                  <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 shadow-sm text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider select-none">
                    <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">File / Collection</th>
                    <th className="py-2.5 px-3.5 bg-gray-50 dark:bg-gray-900">Type</th>
                    <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Current</th>
                    <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Average</th>
                    <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Peak</th>
                    <th className="py-2.5 px-3.5 text-right bg-gray-50 dark:bg-gray-900">Samples</th>
                    <th className="py-2.5 px-3.5 text-center bg-gray-50 dark:bg-gray-900">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
                  {paginatedFiles.length > 0 ? (
                    paginatedFiles.map((f, idx) => (
                      <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                        <td className="py-2.5 px-3.5">
                          <div className="font-bold text-gray-900 dark:text-gray-200 font-mono">{f.file_name}</div>
                          <div className="text-[10px] text-gray-500 dark:text-gray-400">{f.database}</div>
                        </td>
                        <td className="py-2.5 px-3.5">{getTypeBadge(f.type)}</td>
                        <td className="py-2.5 px-3.5 text-right font-mono text-gray-900 dark:text-gray-200">
                          {formatKBps(f.current_kbps)}
                        </td>
                        <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-200">
                          {formatKBps(f.average_kbps)}
                        </td>
                        <td className="py-2.5 px-3.5 text-right font-mono text-amber-600 dark:text-amber-400 font-bold">
                          {formatKBps(f.peak_kbps)}
                        </td>
                        <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                          {f.samples} / {f.total_samples || labels.length}
                        </td>
                        <td className="py-2.5 px-3.5 text-center">{getStatusBadge(f.status)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="py-10 text-center text-gray-500 dark:text-gray-400 italic">
                        {tableSearchQuery
                          ? `No files matching "${tableSearchQuery}".`
                          : 'No database files statistics available.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2 text-xs text-gray-500 dark:text-gray-400">
              <div className="flex items-center gap-3">
                <span>
                  Showing{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {filteredFiles.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0}
                  </span>{' '}
                  to{' '}
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {Math.min(currentPage * itemsPerPage, filteredFiles.length)}
                  </span>{' '}
                  of <span className="font-semibold text-gray-900 dark:text-white">{filteredFiles.length}</span> files
                </span>

                <div className="flex items-center gap-2 border-l border-gray-200 dark:border-gray-800 pl-3">
                  <span className="text-[11px] text-gray-500 dark:text-gray-400">Per page:</span>
                  <ItemsPerPageDropdown
                    value={itemsPerPage}
                    onChange={(val: any) => {
                      setItemsPerPage(Number(val));
                      setCurrentPage(1);
                    }}
                  />
                </div>
              </div>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
                  title="First Page"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
                  title="Previous Page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>

                <span className="px-2 py-0.5 text-xs text-gray-700 dark:text-gray-300 font-medium">
                  Page <span className="text-gray-900 dark:text-white font-bold">{currentPage}</span> of{' '}
                  <span className="text-gray-900 dark:text-white font-bold">{totalPages}</span>
                </span>

                <button
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
                  title="Next Page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:hover:bg-transparent text-gray-600 dark:text-gray-300 cursor-pointer disabled:cursor-not-allowed"
                  title="Last Page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        ) : (
          /* Instance Metrics Detailed Table when files array is empty */
          <div className="overflow-x-auto border border-gray-200 dark:border-gray-700/50 rounded-lg">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-gray-200 dark:border-gray-700/80 bg-gray-50 dark:bg-gray-900 text-[11px] text-gray-600 dark:text-gray-400 uppercase tracking-wider">
                  <th className="py-2.5 px-3.5">Metric</th>
                  <th className="py-2.5 px-3.5">Operation Type</th>
                  <th className="py-2.5 px-3.5 text-right">Current Rate</th>
                  <th className="py-2.5 px-3.5 text-right">Average Rate</th>
                  <th className="py-2.5 px-3.5 text-right">Peak Rate</th>
                  <th className="py-2.5 px-3.5 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700/30 text-gray-800 dark:text-gray-200 bg-white dark:bg-gray-900/40">
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                  <td className="py-2.5 px-3.5 font-bold font-mono text-gray-900 dark:text-white flex items-center gap-2">
                    <Activity className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" />
                    Total File I/O
                  </td>
                  <td className="py-2.5 px-3.5 text-gray-500 dark:text-gray-400">Cumulative (Read + Write)</td>
                  <td className="py-2.5 px-3.5 text-right font-mono font-bold text-gray-900 dark:text-white">
                    {formatKBps(totalCurrentIO)}
                  </td>
                  <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                    {formatKBps(totalAvgIO)}
                  </td>
                  <td className="py-2.5 px-3.5 text-right font-mono font-bold text-blue-600 dark:text-blue-400">
                    {formatKBps(totalPeakIO)}
                  </td>
                  <td className="py-2.5 px-3.5 text-center">{getStatusBadge(summary?.status)}</td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                  <td className="py-2.5 px-3.5 font-bold font-mono text-emerald-700 dark:text-emerald-300 flex items-center gap-2">
                    <ArrowDownToLine className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-400" />
                    Disk Read Rate
                  </td>
                  <td className="py-2.5 px-3.5 text-emerald-600 dark:text-emerald-400/80">WiredTiger Storage Engine Reads</td>
                  <td className="py-2.5 px-3.5 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                    {formatKBps(readMetrics.current)}
                  </td>
                  <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                    {formatKBps(readMetrics.avg)}
                  </td>
                  <td className="py-2.5 px-3.5 text-right font-mono font-bold text-emerald-600 dark:text-emerald-400">
                    {formatKBps(readMetrics.peak)}
                  </td>
                  <td className="py-2.5 px-3.5 text-center">
                    <span className="text-[10px] font-mono text-emerald-700 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-200 dark:border-emerald-500/20">
                      {readPercentage}%
                    </span>
                  </td>
                </tr>
                <tr className="hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors">
                  <td className="py-2.5 px-3.5 font-bold font-mono text-amber-700 dark:text-amber-300 flex items-center gap-2">
                    <ArrowUpFromLine className="w-3.5 h-3.5 text-amber-500 dark:text-amber-400" />
                    Disk Write Rate
                  </td>
                  <td className="py-2.5 px-3.5 text-amber-600 dark:text-amber-400/80">WiredTiger Storage Engine Writes & Journal</td>
                  <td className="py-2.5 px-3.5 text-right font-mono font-bold text-amber-600 dark:text-amber-400">
                    {formatKBps(writeMetrics.current)}
                  </td>
                  <td className="py-2.5 px-3.5 text-right font-mono text-gray-700 dark:text-gray-300">
                    {formatKBps(writeMetrics.avg)}
                  </td>
                  <td className="py-2.5 px-3.5 text-right font-mono font-bold text-amber-600 dark:text-amber-400">
                    {formatKBps(writeMetrics.peak)}
                  </td>
                  <td className="py-2.5 px-3.5 text-center">
                    <span className="text-[10px] font-mono text-amber-700 dark:text-amber-400 font-semibold bg-amber-50 dark:bg-amber-500/10 px-2 py-0.5 rounded border border-amber-200 dark:border-amber-500/20">
                      {writePercentage}%
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default MongoFileIOView;
